import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Shield, Loader2, Mail, Lock, KeyRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function Login() {
  const { user, login, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("password"); // password | passwordless
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [challengeId, setChallengeId] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState(null); // null | "login" | "otp"

  useEffect(() => {
    if (user && user !== false) navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("superseded") === "1") {
      setErr("La tua sessione è stata chiusa perché è stato effettuato l'accesso su un altro dispositivo.");
    }
    if (new URLSearchParams(window.location.search).get("demo_scaduta") === "1") {
      setErr("Demo scaduta");
    }
  }, []);

  const goOtp = (res) => {
    setChallengeId(res.challengeId);
    setStep("otp");
    setCode("");
    setInfo(`Abbiamo inviato un codice a ${res.email}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setLoading(true);
    const res = mode === "password" ? await login(email, password) : await requestOtp(email);
    setLoading(false);
    if (res.sessionConflict) return setConflict("login");
    if (res.ok && res.user) return navigate("/", { replace: true });
    if (res.otpRequired || (res.ok && res.challengeId)) return goOtp(res);
    setErr(res.error);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const res = await verifyOtp(challengeId, code, remember);
    setLoading(false);
    if (res.sessionConflict) return setConflict("otp");
    if (res.ok) navigate("/", { replace: true });
    else setErr(res.error);
  };

  const confirmTakeover = async () => {
    setLoading(true); setErr("");
    const res = conflict === "otp"
      ? await verifyOtp(challengeId, code, remember, true)
      : await login(email, password, true);
    setLoading(false);
    setConflict(null);
    if (res.ok) navigate("/", { replace: true });
    else setErr(res.error || "Impossibile continuare");
  };

  const resendOtp = async () => {
    setErr(""); setInfo(""); setLoading(true);
    const res = mode === "password" ? await login(email, password) : await requestOtp(email);
    setLoading(false);
    if (res.ok && res.user) return navigate("/", { replace: true });
    if (res.otpRequired || (res.ok && res.challengeId)) {
      setChallengeId(res.challengeId);
      setInfo("Nuovo codice inviato.");
    } else setErr(res.error || "Impossibile inviare il codice");
  };

  const backToCredentials = () => { setStep("credentials"); setCode(""); setErr(""); setInfo(""); };

  return (
    <div className="min-h-screen flex bg-background">
      {conflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="session-conflict-modal">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card text-card-foreground p-6 shadow-2xl pgp-fade-up">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-amber-500/15 flex items-center justify-center">
                <Shield className="h-5 w-5 text-amber-500" strokeWidth={2.4} />
              </div>
              <h2 className="font-head text-lg font-bold text-foreground">Sessione già attiva</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Hai già una sessione attiva su un altro dispositivo. Vuoi continuare qui e chiudere l'altra sessione?
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setConflict(null); setLoading(false); }} data-testid="session-conflict-cancel"
                className="border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground">
                Annulla
              </Button>
              <Button type="button" onClick={confirmTakeover} disabled={loading} data-testid="session-conflict-continue"
                className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continua qui"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Left brand panel */}
      <div className="hidden lg:flex w-[46%] relative overflow-hidden border-r border-[#1e293b]">
        <img
          src="https://images.unsplash.com/photo-1772793505600-abf9d6f9a3d5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Studio"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(10,13,20,0.85), rgba(10,13,20,0.97))" }} />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-amber-500 flex items-center justify-center pgp-glow">
              <Shield className="h-6 w-6 text-[#0a0d14]" strokeWidth={2.4} />
            </div>
            <span className="login-left-brand font-head font-extrabold text-xl tracking-tight">
              Patro<span className="text-amber-500">Gest</span>Pro
            </span>
          </div>
          <div>
            <h1 className="login-left-title font-head text-4xl font-extrabold leading-tight tracking-tight">
              Il gestionale per<br />studi di infortunistica<br />
              <span className="text-amber-500">stradale.</span>
            </h1>
            <p className="login-left-subtitle mt-5 max-w-md leading-relaxed">
              Piattaforma SaaS multi-tenant: gestione studi, operatori con permessi granulari,
              area clienti riservata, template lettere e messaggistica interna.
            </p>
          </div>
          <div className="login-left-copyright text-xs font-mono">© 2026 PatroGestPro · Servizi Globali</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 pgp-grid-bg">
        <div className="w-full max-w-sm pgp-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-amber-500 flex items-center justify-center">
              <Shield className="h-5 w-5 text-[#0a0d14]" strokeWidth={2.4} />
            </div>
            <span className="font-head font-extrabold text-lg">
              Patro<span className="text-amber-500">Gest</span>Pro
            </span>
          </div>

          <h2 className="font-head text-2xl font-bold text-slate-50">Accedi</h2>
          <p className="text-sm text-slate-400 mt-1 mb-5">
            {step === "otp" ? "Inserisci il codice ricevuto via email." : "Inserisci le tue credenziali per continuare."}
          </p>

          {step === "credentials" && (
            <>
              <div className="flex rounded-lg border border-slate-700 overflow-hidden mb-5" data-testid="login-mode-toggle">
                <button type="button" onClick={() => { setMode("password"); setErr(""); }} data-testid="mode-password"
                  className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === "password" ? "bg-amber-500 text-[#0a0d14]" : "bg-slate-800 text-white hover:bg-slate-700"}`}>Password</button>
                <button type="button" onClick={() => { setMode("passwordless"); setErr(""); }} data-testid="mode-passwordless"
                  className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === "passwordless" ? "bg-amber-500 text-[#0a0d14]" : "bg-slate-800 text-white hover:bg-slate-700"}`}>Codice via email</button>
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                <div>
                  <Label className="text-slate-300 text-sm">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@studio.it"
                      required
                      data-testid="login-email-input"
                      className="pl-10 bg-slate-900/60 border-slate-700 text-slate-100 h-11"
                    />
                  </div>
                </div>
                {mode === "password" && (
                  <div>
                    <Label className="text-slate-300 text-sm">Password</Label>
                    <div className="relative mt-1.5">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <PasswordInput
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        data-testid="login-password-input"
                        className="pl-10 bg-slate-900/60 border-slate-700 text-slate-100 h-11"
                      />
                    </div>
                  </div>
                )}

                {err && (
                  <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2" data-testid="login-error">
                    {err}
                  </div>
                )}

                {mode === "password" && (
                  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="login-remember-label">
                    <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember" />
                    Ricordami per 30 giorni su questo dispositivo
                  </label>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "password" ? "Accedi" : "Invia codice")}
                </Button>
              </form>
            </>
          )}

          {step === "otp" && (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="otp-form">
              {info && (
                <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2" data-testid="otp-info">{info}</div>
              )}
              <div>
                <Label className="text-slate-300 text-sm">Codice di verifica</Label>
                <div className="relative mt-1.5">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    required
                    autoFocus
                    name="pgp-otp"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="otp-code-input"
                    className="pl-10 bg-slate-900/60 border-slate-700 text-slate-100 h-11 tracking-[0.4em] font-mono text-lg"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="otp-remember" />
                Ricordami per 30 giorni su questo dispositivo
              </label>

              {err && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>
              )}

              <Button
                type="submit"
                disabled={loading || code.length < 6}
                data-testid="otp-submit-button"
                className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verifica e accedi"}
              </Button>

              <div className="flex items-center justify-between text-sm pt-1">
                <button type="button" onClick={backToCredentials} data-testid="otp-back" className="text-slate-400 hover:text-slate-200 transition-colors">← Indietro</button>
                <button type="button" onClick={resendOtp} disabled={loading} data-testid="otp-resend" className="text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50">Invia di nuovo</button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
